/**
 * Browser mirror of LottoNumberGenerator.  It intentionally uses only stable
 * integer arithmetic so Android and Web can verify the same seed with golden
 * vectors.  Do not substitute Math.random() here.
 */
export const LOTTO_NUMBER_ENGINE_VERSION = "lotto-number-engine-v1";

export type PickStrategy = "UNIFORM" | "BALANCED" | "HOT" | "NEARBY" | "MIXED";

export interface PickConstraints {
  readonly included?: ReadonlySet<number>;
  readonly excluded?: ReadonlySet<number>;
}

export interface NumberFrequency { readonly number: number; readonly count: number; readonly sampleDrawCount: number }

export interface NumberDraw { readonly numbers: readonly number[]; readonly bonusNumber: number }

export function generateLottoPick(
  strategy: PickStrategy,
  seed: number,
  constraints: PickConstraints = {},
  weights: ReadonlyMap<number, number> = new Map(),
): readonly number[] {
  validateConstraints(constraints);
  const random = new LottoPickRandom(seed);
  switch (strategy) {
    case "UNIFORM": return uniform(random, constraints);
    case "BALANCED": return balanced(random, constraints);
    case "HOT":
    case "NEARBY":
    case "MIXED": return weighted(random, constraints, weights);
  }
}

export function numberFrequency(draws: readonly NumberDraw[]): readonly NumberFrequency[] {
  const counts = Array<number>(46).fill(0);
  for (const draw of draws) {
    validateDraw(draw);
    for (const number of draw.numbers) counts[number] += 1;
  }
  return Array.from({ length: 45 }, (_, index) => ({ number: index + 1, count: counts[index + 1]!, sampleDrawCount: draws.length }));
}

export function rankFrequency(draws: readonly NumberDraw[]): readonly NumberFrequency[] {
  return [...numberFrequency(draws)].sort((left, right) => right.count - left.count || left.number - right.number);
}

function uniform(random: LottoPickRandom, constraints: PickConstraints): readonly number[] {
  const result = new Set(constraints.included ?? []);
  const candidates = candidatesFor(result, constraints);
  random.shuffle(candidates);
  for (const number of candidates.slice(0, 6 - result.size)) result.add(number);
  return [...result].sort((left, right) => left - right);
}

function balanced(random: LottoPickRandom, constraints: PickConstraints): readonly number[] {
  for (let attempt = 0; attempt < 500; attempt += 1) {
    const candidate = uniform(random, constraints);
    const oddCount = candidate.filter((number) => number % 2 === 1).length;
    const lowCount = candidate.filter((number) => number <= 22).length;
    const sum = candidate.reduce((total, number) => total + number, 0);
    if (oddCount >= 2 && oddCount <= 4 && lowCount >= 2 && lowCount <= 4 && sum >= 100 && sum <= 175) return candidate;
  }
  throw new Error("현재 조건으로 균형 조합을 만들 수 없습니다.");
}

function weighted(random: LottoPickRandom, constraints: PickConstraints, weights: ReadonlyMap<number, number>): readonly number[] {
  const result = new Set(constraints.included ?? []);
  const candidates = candidatesFor(result, constraints);
  while (result.size < 6 && candidates.length) {
    const totalWeight = candidates.reduce((total, number) => total + Math.max(weights.get(number) ?? 0, 1), 0);
    let cursor = random.nextInt(totalWeight);
    const selected = candidates.find((number) => {
      cursor -= Math.max(weights.get(number) ?? 0, 1);
      return cursor < 0;
    });
    if (selected === undefined) throw new Error("lotto_pick_weight_selection_failed");
    result.add(selected);
    candidates.splice(candidates.indexOf(selected), 1);
  }
  return [...result].sort((left, right) => left - right);
}

function candidatesFor(result: ReadonlySet<number>, constraints: PickConstraints): number[] {
  const excluded = constraints.excluded ?? new Set<number>();
  return Array.from({ length: 45 }, (_, index) => index + 1).filter((number) => !excluded.has(number) && !result.has(number));
}

function validateConstraints(constraints: PickConstraints): void {
  const included = constraints.included ?? new Set<number>();
  const excluded = constraints.excluded ?? new Set<number>();
  if (included.size > 6) throw new Error("포함 번호는 최대 6개입니다.");
  if ([...included, ...excluded].some((number) => !Number.isInteger(number) || number < 1 || number > 45)) throw new Error("번호는 1~45 범위여야 합니다.");
  if ([...included].some((number) => excluded.has(number))) throw new Error("같은 번호를 포함과 제외에 동시에 지정할 수 없습니다.");
  if (45 - excluded.size < 6) throw new Error("선택 가능한 번호가 6개 미만입니다.");
}

function validateDraw(draw: NumberDraw): void {
  if (draw.numbers.length !== 6 || new Set(draw.numbers).size !== 6 || draw.numbers.some((number) => !Number.isInteger(number) || number < 1 || number > 45)) throw new Error("본번호가 올바르지 않습니다.");
  if (!Number.isInteger(draw.bonusNumber) || draw.bonusNumber < 1 || draw.bonusNumber > 45 || draw.numbers.includes(draw.bonusNumber)) throw new Error("보너스 번호가 올바르지 않습니다.");
}

class LottoPickRandom {
  private state: number;

  constructor(seed: number) { this.state = seed >>> 0; }

  nextInt(bound: number): number {
    if (!Number.isInteger(bound) || bound <= 0) throw new Error("bound must be positive");
    return Math.floor((this.nextUInt() / 0x1_0000_0000) * bound);
  }

  shuffle(values: number[]): void {
    for (let index = values.length - 1; index >= 1; index -= 1) {
      const swapIndex = this.nextInt(index + 1);
      [values[index], values[swapIndex]] = [values[swapIndex]!, values[index]!];
    }
  }

  private nextUInt(): number {
    let value = (this.state + 0x6D2B79F5) >>> 0;
    this.state = value;
    value = Math.imul(value ^ (value >>> 15), value | 1) >>> 0;
    value = (value ^ ((value + Math.imul(value ^ (value >>> 7), value | 61)) >>> 0)) >>> 0;
    return (value ^ (value >>> 14)) >>> 0;
  }
}
